import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Shield, Heart, Home, Car, Trash2, Edit2, 
  DollarSign, Calendar, User, Phone, FileText, Sparkles,
  Building2, MapPin, ChevronDown, ChevronUp, Upload, File, X, Download, Lock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import { SimsConfirmDialog } from '../components/SimsDialog';
import TrustedProviders from '../components/TrustedProviders';
import PlaceFinder from '../components/PlaceFinder';
import SecurityVerification, { useSecurityStatus, isSecurityVerified } from '../components/SecurityVerification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sims-style Plumbob
const Plumbob = ({ color = 'blue', size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  const colors = {
    green: ['#4ade80', '#22c55e', '#16a34a'],
    blue: ['#60a5fa', '#3b82f6', '#2563eb'],
    purple: ['#3A5C8F', '#2B4C7E', '#1E3A63'],
    pink: ['#D0B384', '#C8A97E', '#B3956B'],
    amber: ['#fbbf24', '#f59e0b', '#d97706']
  };
  const [c1, c2, c3] = colors[color] || colors.blue;
  
  return (
    <motion.svg
      viewBox="0 0 24 24"
      className={sizes[size]}
      animate={{ rotateY: [0, 180, 360] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      <defs>
        <linearGradient id={`plumbob-ins-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c1} />
          <stop offset="50%" stopColor={c2} />
          <stop offset="100%" stopColor={c3} />
        </linearGradient>
      </defs>
      <polygon
        points="12,2 22,12 12,22 2,12"
        fill={`url(#plumbob-ins-${color})`}
        stroke={c3}
        strokeWidth="1"
      />
    </motion.svg>
  );
};

// Insurance type configurations
const insuranceTypes = {
  life: {
    icon: Heart,
    color: 'from-[#C8A97E] to-[#C8A97E]',
    bg: 'from-[#F7F1E8] to-[#F7F1E8]',
    border: 'border-[#EAD9BF]',
    plumbob: 'pink',
    label: 'Life Insurance',
    description: 'Protect your loved ones'
  },
  home: {
    icon: Home,
    color: 'from-[#C8A97E] to-[#C8A97E]',
    bg: 'from-[#F7F1E8] to-[#F7F1E8]',
    border: 'border-[#EAD9BF]',
    plumbob: 'amber',
    label: 'Home Insurance',
    description: 'Protect your property'
  },
  car: {
    icon: Car,
    color: 'from-[#2B4C7E] to-[#2B4C7E]',
    bg: 'from-[#EBF0F5] to-[#EBF0F5]',
    border: 'border-[#D7E0EA]',
    plumbob: 'blue',
    label: 'Car Insurance',
    description: 'Protect your vehicle'
  }
};

// Policy card component
function PolicyCard({ policy, type, onEdit, onDelete, onUploadDoc, documents = [] }) {
  const [expanded, setExpanded] = useState(false);
  const config = insuranceTypes[type];
  const Icon = config.icon;
  const fileInputRef = useState(null);
  const policyDocs = documents.filter(d => d.insurance_id === policy.insurance_id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Card className={`rounded-3xl border border-gray-100 bg-white shadow-md overflow-hidden`}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 bg-gradient-to-br ${config.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                <Icon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{policy.provider}</h3>
                <p className="text-sm text-gray-500">Policy: {policy.policy_number}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(policy)}
                className="rounded-full hover:bg-white/50"
              >
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(policy)}
                className="rounded-full hover:bg-red-100 text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Coverage</p>
              <p className={`text-2xl font-bold bg-gradient-to-r ${config.color} bg-clip-text text-transparent`}>
                ${policy.coverage_amount?.toLocaleString()}
              </p>
            </div>
            {policy.premium_amount && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Premium</p>
                <p className="text-lg font-semibold text-gray-700">
                  ${policy.premium_amount}/
                  <span className="text-sm">{policy.payment_frequency || 'mo'}</span>
                </p>
              </div>
            )}
          </div>

          {/* Type-specific info */}
          {type === 'life' && policy.beneficiary_name && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <User className="h-4 w-4" />
              <span>Beneficiary: {policy.beneficiary_name}</span>
            </div>
          )}
          {type === 'home' && policy.property_address && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <MapPin className="h-4 w-4" />
              <span>{policy.property_address}</span>
            </div>
          )}
          {type === 'car' && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
              <Car className="h-4 w-4" />
              <span>{policy.vehicle_year} {policy.vehicle_make} {policy.vehicle_model}</span>
            </div>
          )}

          {/* Expandable details */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="w-full mt-2 text-gray-500 hover:text-gray-700"
          >
            {expanded ? (
              <>Less details <ChevronUp className="h-4 w-4 ml-1" /></>
            ) : (
              <>More details <ChevronDown className="h-4 w-4 ml-1" /></>
            )}
          </Button>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 border-t border-gray-200 mt-4 space-y-2 text-sm">
                  {policy.agent_name && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="h-4 w-4" />
                      <span>Agent: {policy.agent_name}</span>
                    </div>
                  )}
                  {policy.agent_phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-4 w-4" />
                      <span>{policy.agent_phone}</span>
                    </div>
                  )}
                  {policy.deductible && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="h-4 w-4" />
                      <span>Deductible: ${policy.deductible}</span>
                    </div>
                  )}
                  {policy.policy_start_date && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Start: {new Date(policy.policy_start_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {policy.policy_end_date && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>End: {new Date(policy.policy_end_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  {policy.notes && (
                    <div className="flex items-start gap-2 text-gray-600">
                      <FileText className="h-4 w-4 mt-0.5" />
                      <span>{policy.notes}</span>
                    </div>
                  )}
                  {type === 'car' && policy.vin_number && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">VIN: {policy.vin_number}</span>
                    </div>
                  )}
                  
                  {/* Document Upload Section */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-700 flex items-center gap-2">
                        <File className="h-4 w-4" />
                        Policy Documents
                      </h4>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => onUploadDoc(policy.insurance_id, type, e.target.files[0])}
                        />
                        <span className="text-xs text-[#2B4C7E] hover:text-[#1E3A63] font-medium flex items-center gap-1">
                          <Upload className="h-3 w-3" />
                          Upload
                        </span>
                      </label>
                    </div>
                    {policyDocs.length === 0 ? (
                      <p className="text-xs text-gray-400">No documents uploaded yet</p>
                    ) : (
                      <div className="space-y-2">
                        {policyDocs.map((doc) => (
                          <div key={doc.document_id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600 truncate max-w-[150px]">{doc.file_name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <a 
                                href={`data:${doc.file_type};base64,${doc.file_data}`}
                                download={doc.file_name}
                                className="text-[#2B4C7E] hover:text-[#2B4C7E] p-1"
                              >
                                <Download className="h-3 w-3" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>
    </motion.div>
  );
}

export default function Insurance() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('life');
  const [policies, setPolicies] = useState({ life: [], home: [], car: [] });
  const [documents, setDocuments] = useState([]);
  
  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  // Security verification state
  const [securityVerificationOpen, setSecurityVerificationOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const { hasPin } = useSecurityStatus();

  // Form state
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [lifeRes, homeRes, carRes, userRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/insurance/life`, { headers }),
        axios.get(`${BACKEND_URL}/api/insurance/home`, { headers }),
        axios.get(`${BACKEND_URL}/api/insurance/car`, { headers }),
        axios.get(`${BACKEND_URL}/api/auth/me`, { headers })
      ]);

      setPolicies({
        life: lifeRes.data,
        home: homeRes.data,
        car: carRes.data
      });
      setUser(userRes.data);
    } catch (error) {
      console.error('Failed to load insurance data:', error);
      toast.error('Failed to load insurance data');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFormData = (type) => {
    const base = {
      policy_number: '',
      provider: '',
      coverage_amount: '',
      premium_amount: '',
      payment_frequency: 'monthly',
      policy_start_date: '',
      policy_end_date: '',
      agent_name: '',
      agent_phone: '',
      notes: ''
    };

    if (type === 'life') {
      return { ...base, beneficiary_name: '', beneficiary_contact: '' };
    } else if (type === 'home') {
      return { ...base, property_address: '', deductible: '', coverage_type: 'homeowners' };
    } else {
      return { ...base, vehicle_make: '', vehicle_model: '', vehicle_year: '', vin_number: '', deductible: '', coverage_type: 'comprehensive' };
    }
  };

  const openAddModal = () => {
    setFormData(getDefaultFormData(activeTab));
    setAddModalOpen(true);
  };

  const openDeleteDialog = (policy) => {
    // Check if security verification is required
    if (hasPin && !isSecurityVerified()) {
      setSelectedPolicy(policy);
      setPendingAction({ type: 'delete', policy });
      setSecurityVerificationOpen(true);
      return;
    }
    setSelectedPolicy(policy);
    setDeleteDialogOpen(true);
  };

  const openEditModal = (policy) => {
    // Check if security verification is required
    if (hasPin && !isSecurityVerified()) {
      setPendingAction({ type: 'edit', policy });
      setSecurityVerificationOpen(true);
      return;
    }
    setSelectedPolicy(policy);
    setFormData(policy);
    setEditModalOpen(true);
  };

  const handleSecurityVerified = () => {
    setSecurityVerificationOpen(false);
    if (pendingAction) {
      if (pendingAction.type === 'delete') {
        setSelectedPolicy(pendingAction.policy);
        setDeleteDialogOpen(true);
      } else if (pendingAction.type === 'edit') {
        setSelectedPolicy(pendingAction.policy);
        setFormData(pendingAction.policy);
        setEditModalOpen(true);
      }
      setPendingAction(null);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem('session_token');
    setSaving(true);

    try {
      const payload = { ...formData };
      // Convert string numbers to actual numbers
      if (payload.coverage_amount) payload.coverage_amount = parseFloat(payload.coverage_amount);
      if (payload.premium_amount) payload.premium_amount = parseFloat(payload.premium_amount);
      if (payload.deductible) payload.deductible = parseFloat(payload.deductible);
      if (payload.vehicle_year) payload.vehicle_year = parseInt(payload.vehicle_year);

      if (editModalOpen && selectedPolicy) {
        await axios.put(
          `${BACKEND_URL}/api/insurance/${activeTab}/${selectedPolicy.insurance_id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('✨ Policy updated successfully!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/insurance/${activeTab}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('🎉 Policy added successfully!');
      }

      setAddModalOpen(false);
      setEditModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPolicy) return;
    
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(
        `${BACKEND_URL}/api/insurance/${activeTab}/${selectedPolicy.insurance_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Policy deleted');
      setDeleteDialogOpen(false);
      setSelectedPolicy(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete policy:', error);
      toast.error('Failed to delete policy');
    }
  };

  const uploadDocument = async (insuranceId, insuranceType, file) => {
    if (!file) return;
    
    const token = localStorage.getItem('session_token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${BACKEND_URL}/api/insurance/${insuranceType}/${insuranceId}/document`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      toast.success('📄 Document uploaded!');
      loadDocuments();
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const loadDocuments = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const allDocs = [];
      
      // Load docs for all policies
      for (const type of ['life', 'home', 'car']) {
        for (const policy of policies[type]) {
          try {
            const res = await axios.get(
              `${BACKEND_URL}/api/insurance/${type}/${policy.insurance_id}/documents`,
              { headers }
            );
            allDocs.push(...res.data);
          } catch (e) {
            // Policy might not have documents endpoint yet
          }
        }
      }
      setDocuments(allDocs);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  useEffect(() => {
    if (policies.life.length || policies.home.length || policies.car.length) {
      loadDocuments();
    }
  }, [policies]);

  const getTotalCoverage = () => {
    return [...policies.life, ...policies.home, ...policies.car]
      .reduce((sum, p) => sum + (p.coverage_amount || 0), 0);
  };

  const getTotalPolicies = () => {
    return policies.life.length + policies.home.length + policies.car.length;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="w-16 h-16 bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <p className="text-gray-600 font-medium">Loading your insurance...</p>
        </motion.div>
      </div>
    );
  }

  const config = insuranceTypes[activeTab];

  return (
    <div className="min-h-screen bg-white" data-testid="insurance-page">
      {/* Subtle top accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#2B4C7E] via-[#2B4C7E] to-[#2B4C7E]" />
      
      <PageHeader 
        title="Insurance Hub" 
        user={user}
        titleIcon={<Shield className="h-6 w-6 text-[#2B4C7E]" />}
        actions={
          <Button 
            onClick={openAddModal}
            className={`bg-gradient-to-r ${config.color} text-white rounded-full hover:opacity-90 shadow-lg text-sm sm:text-base`}
            data-testid="add-policy-btn"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add {config.label}</span>
            <span className="sm:hidden">Add</span>
          </Button>
        }
      />

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Find an Insurance company near you */}
        <Card className="p-6 rounded-3xl border border-gray-100 bg-white shadow-lg mb-8" data-testid="insurance-finder-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E] rounded-xl flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Find an Insurance Company Near You</h2>
              <p className="text-sm text-gray-500">Search insurance agencies by ZIP code, city, or your current location.</p>
            </div>
          </div>
          <PlaceFinder
            category="insurance" noun="insurance agencies" saveLabel="Save"
            savedTitle="My saved insurance offices" testid="insurance-finder"
            disclaimer="Listings are for convenience only. EasyPassing does not endorse, guarantee, or control any insurance company or agency."
          />
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-6 rounded-3xl border border-gray-100 bg-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Total Coverage</span>
                <div className="w-10 h-10 bg-gradient-to-br from-[#2B4C7E] to-[#2B4C7E] rounded-xl flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold text-gray-800">
                ${getTotalCoverage().toLocaleString()}
              </p>
              <p className="text-xs text-[#2B4C7E] mt-2 flex items-center gap-1">
                <Shield className="h-3 w-3" /> Protected assets
              </p>
            </Card>
          </motion.div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="p-6 rounded-3xl border border-gray-100 bg-white shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Active Policies</span>
                <div className="w-10 h-10 bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold text-gray-800">
                {getTotalPolicies()}
              </p>
              <p className="text-xs text-[#2B4C7E] mt-2 flex items-center gap-1">
                <FileText className="h-3 w-3" /> Insurance policies
              </p>
            </Card>
          </motion.div>
        </div>

        {/* Trusted Providers Section - Show when no policies exist */}
        {getTotalPolicies() === 0 && (
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <TrustedProviders 
              category="insurance"
              title="Need Insurance? Browse Trusted Providers"
              description="Compare reputable insurance companies with reviews, pricing, and contact info"
              icon={Shield}
            />
          </motion.div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-8 bg-gray-50 rounded-2xl p-1 border border-gray-100">
            {Object.entries(insuranceTypes).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  className={`rounded-xl py-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-gray-800 transition-all font-medium`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {cfg.label}
                  {policies[key].length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-gray-200 data-[state=active]:bg-[#EBF0F5] rounded-full text-xs">
                      {policies[key].length}
                    </span>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.keys(insuranceTypes).map((type) => (
            <TabsContent key={type} value={type}>
              {policies[type].length === 0 ? (
                <motion.div 
                  className="text-center py-16"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className={`w-24 h-24 bg-gradient-to-br ${insuranceTypes[type].bg} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    {(() => {
                      const Icon = insuranceTypes[type].icon;
                      return <Icon className="h-12 w-12 text-gray-400" strokeWidth={1.5} />;
                    })()}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-3">
                    No {insuranceTypes[type].label} Yet
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">
                    Add your {insuranceTypes[type].label.toLowerCase()} policies to keep all your important information in one place.
                  </p>
                  <Button
                    onClick={openAddModal}
                    className={`bg-gradient-to-r ${insuranceTypes[type].color} text-white rounded-full shadow-lg`}
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Add Your First Policy
                  </Button>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence>
                    {policies[type].map((policy) => (
                      <PolicyCard
                        key={policy.insurance_id}
                        policy={policy}
                        type={type}
                        onEdit={openEditModal}
                        onDelete={openDeleteDialog}
                        onUploadDoc={uploadDocument}
                        documents={documents}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={addModalOpen || editModalOpen} onOpenChange={() => { setAddModalOpen(false); setEditModalOpen(false); }}>
        <DialogContent className="max-w-lg bg-white border border-gray-200 rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center gap-3 pb-4 border-b border-gray-100">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center`}>
              <config.icon className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-800">
              {editModalOpen ? 'Edit' : 'Add'} {config.label}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Common fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number *</label>
                <Input
                  placeholder="POL-123456"
                  value={formData.policy_number || ''}
                  onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
                <Input
                  placeholder="Insurance Company"
                  value={formData.provider || ''}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coverage Amount *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="100000"
                    value={formData.coverage_amount || ''}
                    onChange={(e) => setFormData({ ...formData, coverage_amount: e.target.value })}
                    className="pl-9 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Premium</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="150"
                    value={formData.premium_amount || ''}
                    onChange={(e) => setFormData({ ...formData, premium_amount: e.target.value })}
                    className="pl-9 rounded-xl"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Frequency</label>
              <Select value={formData.payment_frequency || 'monthly'} onValueChange={(v) => setFormData({ ...formData, payment_frequency: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Type-specific fields */}
            {activeTab === 'life' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Beneficiary Name</label>
                    <Input
                      placeholder="John Doe"
                      value={formData.beneficiary_name || ''}
                      onChange={(e) => setFormData({ ...formData, beneficiary_name: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Beneficiary Contact</label>
                    <Input
                      placeholder="Phone or Email"
                      value={formData.beneficiary_contact || ''}
                      onChange={(e) => setFormData({ ...formData, beneficiary_contact: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'home' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Property Address *</label>
                  <Input
                    placeholder="123 Main St, City, State 12345"
                    value={formData.property_address || ''}
                    onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Coverage Type</label>
                    <Select value={formData.coverage_type || 'homeowners'} onValueChange={(v) => setFormData({ ...formData, coverage_type: v })}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="homeowners">Homeowners</SelectItem>
                        <SelectItem value="renters">Renters</SelectItem>
                        <SelectItem value="condo">Condo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deductible</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="number"
                        placeholder="1000"
                        value={formData.deductible || ''}
                        onChange={(e) => setFormData({ ...formData, deductible: e.target.value })}
                        className="pl-9 rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'car' && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                    <Input
                      type="number"
                      placeholder="2024"
                      value={formData.vehicle_year || ''}
                      onChange={(e) => setFormData({ ...formData, vehicle_year: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Make *</label>
                    <Input
                      placeholder="Toyota"
                      value={formData.vehicle_make || ''}
                      onChange={(e) => setFormData({ ...formData, vehicle_make: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                    <Input
                      placeholder="Camry"
                      value={formData.vehicle_model || ''}
                      onChange={(e) => setFormData({ ...formData, vehicle_model: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">VIN Number</label>
                    <Input
                      placeholder="1HGBH41JXMN109186"
                      value={formData.vin_number || ''}
                      onChange={(e) => setFormData({ ...formData, vin_number: e.target.value })}
                      className="rounded-xl font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Coverage Type</label>
                    <Select value={formData.coverage_type || 'comprehensive'} onValueChange={(v) => setFormData({ ...formData, coverage_type: v })}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="liability">Liability Only</SelectItem>
                        <SelectItem value="collision">Collision</SelectItem>
                        <SelectItem value="comprehensive">Comprehensive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deductible</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      placeholder="500"
                      value={formData.deductible || ''}
                      onChange={(e) => setFormData({ ...formData, deductible: e.target.value })}
                      className="pl-9 rounded-xl"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Policy dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <Input
                  type="date"
                  value={formData.policy_start_date || ''}
                  onChange={(e) => setFormData({ ...formData, policy_start_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <Input
                  type="date"
                  value={formData.policy_end_date || ''}
                  onChange={(e) => setFormData({ ...formData, policy_end_date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Agent info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                <Input
                  placeholder="Jane Smith"
                  value={formData.agent_name || ''}
                  onChange={(e) => setFormData({ ...formData, agent_name: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Agent Phone</label>
                <Input
                  placeholder="(555) 123-4567"
                  value={formData.agent_phone || ''}
                  onChange={(e) => setFormData({ ...formData, agent_phone: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <Textarea
                placeholder="Any additional notes about this policy..."
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-xl"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              onClick={handleSave}
              disabled={saving || !formData.policy_number || !formData.provider || !formData.coverage_amount}
              className={`w-full bg-gradient-to-r ${config.color} text-white rounded-full py-6`}
            >
              {saving ? (
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5" />
                  Saving...
                </motion.div>
              ) : (
                <>
                  <Shield className="h-5 w-5 mr-2" />
                  {editModalOpen ? 'Update' : 'Add'} Policy
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <SimsConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Policy?"
        message={`Are you sure you want to delete this ${config.label.toLowerCase()} policy from ${selectedPolicy?.provider}? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Keep It"
        variant="error"
      />

      {/* Security Verification */}
      <SecurityVerification
        open={securityVerificationOpen}
        onClose={() => {
          setSecurityVerificationOpen(false);
          setPendingAction(null);
        }}
        onVerified={handleSecurityVerified}
        title="Verify to Modify Insurance"
        description="Enter your security PIN to edit or delete insurance policies"
      />
    </div>
  );
}
